/**
 * Ad Tracking Utility
 * Captures and persists Google Ads (gclid) and UTM parameters across the user journey.
 * Uses cookies for cross-request access and server-side reading.
 */

const COOKIE_EXPIRY_DAYS = 90; // Google's attribution window
const AD_PARAMS = ['gclid', 'gbraid', 'wbraid']; // Google ad identifiers
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * Set a cookie with the specified name, value, and expiry
 */
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  // SameSite=Lax allows the cookie to be sent on top-level navigations
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

/**
 * Get a cookie value by name
 */
const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
};

/**
 * Capture ad and UTM parameters from URL and store in cookies.
 * Only stores parameters that are present - does NOT overwrite existing values
 * if the user navigates without parameters (preserves original attribution).
 *
 * Call this on app initialization.
 */
export const captureAdParams = () => {
  const params = new URLSearchParams(window.location.search);

  // Capture Google ad identifiers (gclid, gbraid, wbraid)
  AD_PARAMS.forEach((param) => {
    const value = params.get(param);
    if (value) {
      setCookie(param, value, COOKIE_EXPIRY_DAYS);
      // Also store timestamp for the primary gclid
      if (param === 'gclid') {
        setCookie('gclid_timestamp', Date.now().toString(), COOKIE_EXPIRY_DAYS);
      }
    }
  });

  // Capture UTM parameters
  UTM_PARAMS.forEach((param) => {
    const value = params.get(param);
    if (value) {
      setCookie(param, value, COOKIE_EXPIRY_DAYS);
    }
  });
};

/**
 * Get the stored gclid (Google Click ID) if available and not expired.
 * Returns null for organic visitors or if the gclid has expired.
 */
export const getStoredGclid = () => {
  return getCookie('gclid');
};

/**
 * Get all stored ad parameters as an object.
 * Returns only the parameters that exist - empty object for organic visitors.
 */
export const getStoredAdParams = () => {
  const result = {};

  [...AD_PARAMS, ...UTM_PARAMS].forEach((param) => {
    const value = getCookie(param);
    if (value) {
      result[param] = value;
    }
  });

  return result;
};

/**
 * Check if the user came from a paid ad (has gclid or other ad identifiers)
 */
export const isFromPaidAd = () => {
  return AD_PARAMS.some((param) => getCookie(param) !== null);
};

/**
 * Get tracking data formatted for API requests.
 * Safe to call for both ad and organic visitors.
 */
export const getTrackingDataForApi = () => {
  const params = getStoredAdParams();

  // Only return tracking data if there's something to track
  if (Object.keys(params).length === 0) {
    return null;
  }

  return {
    ...params,
    captured_at: getCookie('gclid_timestamp') || null,
  };
};

/**
 * Clear all tracking cookies (useful for testing or user privacy requests)
 */
export const clearTrackingData = () => {
  [...AD_PARAMS, ...UTM_PARAMS, 'gclid_timestamp'].forEach((param) => {
    document.cookie = `${param}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
};
